<template>
  <SidebarApplication
    :workspace="workspace"
    :application="application"
    @selected="selected"
  >
    <template v-if="isAppSelected(application)" #body>
      <ul class="tree__subs">
        <SidebarItem
          v-for="table in orderedTables"
          :key="table.id"
          v-sortable="{
            id: table.id,
            update: orderTables,
            marginTop: -1.5,
            enabled: $hasPermission(
              'database.order_tables',
              application,
              application.workspace.id
            ),
          }"
          :database="application"
          :table="table"
        ></SidebarItem>
      </ul>
      <ul v-if="pendingJobs.length" class="tree__subs">
        <component
          :is="getPendingJobComponent(job)"
          v-for="job in pendingJobs"
          :key="job.id"
          :job="job"
        >
        </component>
      </ul>
      <a
        v-if="
          $hasPermission(
            'database.create_table',
            application,
            application.workspace.id
          )
        "
        class="tree__sub-add"
        data-highlight="create-table"
        @click="$refs.createTableModal.show()"
      >
        <i class="tree__sub-add-icon iconoir-plus"></i>
        {{ $t('sidebar.createTable') }}
      </a>
      <!-- Origem Flow: projetos encerrados — arquivados somem da árvore mas
           ficam aqui, consultáveis e reversíveis. -->
      <template v-if="archivedTables.length">
        <a
          class="tree__sub-add sidebar__archived-toggle"
          @click="showArchived = !showArchived"
        >
          <i
            class="tree__sub-add-icon"
            :class="
              showArchived
                ? 'iconoir-nav-arrow-down'
                : 'iconoir-nav-arrow-right'
            "
          ></i>
          {{ $t('sidebar.archivedTables', { count: archivedTables.length }) }}
        </a>
        <ul v-if="showArchived" class="tree__subs sidebar__archived-list">
          <SidebarItem
            v-for="table in archivedTables"
            :key="'archived-' + table.id"
            :database="application"
            :table="table"
          ></SidebarItem>
        </ul>
      </template>
      <CreateTableModal ref="createTableModal" :database="application" />
    </template>
  </SidebarApplication>
</template>

<script>
import { mapGetters } from 'vuex'
import { notifyIf } from '@baserow/modules/core/utils/error'
import SidebarItem from '@baserow/modules/database/components/sidebar/SidebarItem'
import SidebarApplication from '@baserow/modules/core/components/sidebar/SidebarApplication'
import CreateTableModal from '@baserow/modules/database/components/table/CreateTableModal'

export default {
  name: 'DBSidebar',
  components: {
    CreateTableModal,
    SidebarApplication,
    SidebarItem,
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
    workspace: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Origem Flow: lista de arquivadas recolhida por padrão.
      showArchived: false,
    }
  },
  computed: {
    // Origem Flow: a tabela "Todas as tarefas" (espelho agregado) não entra
    // na árvore — ela é o link "Todas as tarefas" no topo dos Espaços.
    orderedTables() {
      return this.application.tables
        .map((table) => table)
        .filter((table) => !table.archived && !table.all_tasks)
        .sort((a, b) => a.order - b.order)
    },
    archivedTables() {
      return this.application.tables
        .map((table) => table)
        .filter((table) => table.archived && !table.all_tasks)
        .sort((a, b) => a.order - b.order)
    },
    pendingJobs() {
      return this.$store.getters['job/getAll'].filter((job) =>
        this.$registry
          .get('job', job.type)
          .isJobPartOfApplication(job, this.application)
      )
    },
    ...mapGetters({ isAppSelected: 'application/isSelected' }),
  },
  mounted() {
    this.fetchTableCounts()
    // Mantém os badges razoavelmente frescos; o TTL do store deduplica as
    // chamadas entre as várias instâncias (uma por database).
    this.$el.tableCountsInterval = setInterval(() => {
      this.fetchTableCounts()
    }, 60 * 1000)
    this.$bus.$on('table-refresh', this.forceFetchTableCounts)
  },
  beforeUnmount() {
    clearInterval(this.$el.tableCountsInterval)
    this.$bus.$off('table-refresh', this.forceFetchTableCounts)
  },
  methods: {
    fetchTableCounts(force = false) {
      this.$store
        .dispatch('tableCounts/fetch', {
          workspaceId: this.application.workspace.id,
          force,
        })
        .catch(() => {
          // Badge é informativo; falha silenciosa para não poluir com toasts.
        })
    },
    forceFetchTableCounts() {
      this.fetchTableCounts(true)
    },
    async selected(application) {
      try {
        // Origem Flow: clicar no espaço já aberto recolhe a lista de
        // tabelas (toggle, como no ClickUp).
        if (this.isAppSelected(application)) {
          await this.$store.dispatch('application/unselect')
        } else {
          await this.$store.dispatch('application/select', application)
        }
      } catch (error) {
        notifyIf(error, 'workspace')
      }
    },
    async orderTables(order, oldOrder) {
      try {
        await this.$store.dispatch('table/order', {
          database: this.application,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'table')
      }
    },
    getPendingJobComponent(job) {
      return this.$registry.get('job', job.type).getSidebarComponent()
    },
  },
}
</script>
